/**
 * Infers that the error message is related to chrome autoplay
 * policy, based on the *absence* of the words "pause" or "load".
 *
 * The intention is the filter out the following messages:
 *
 * - `{message: 'The play() request was interrupted by a call to pause().'}`
 * - `{message: 'The play() request was interrupted by a new load request.'}`
 *
 * And catch the following messages:
 *
 * - `{message: 'play() failed because the user didn't interact with the document first'}` - Chrome
 * - `{name: 'AbortError', message: The operation was aborted.'}` - Safari
 * - `{
 *      name: 'NotAllowedError',
 *      message: 'The request is not allowed by the user agent or the platform in the current context,
 *                possibly because the user denied permission.'
 *    }` (this is a pre-warning in Safari, not the actual error, which is shown below)
 *
 * Because Chrome's error messages are locale specific (e.g. may not be
 * in English), we are assuming that the presence of `pause` or `load`
 * would be consistent across locales. This may not be true.
 *
 * NB: In Safari, the second `AbortError` in a series of "Safari errors" should be ignored
 * (via the debounce logic below). This appears to be an false emission of unknown
 * origin (probably a bug in Safari) and can break the intended logic, as we will identify
 * it as a rejection of the second play attempt, and pause the player, this corrupting the VST metric.
 *
 * Please see the accompanying tests for all possible scenarios we are trying to catch.
 */

const SAFARI_ERROR_NAME_NOT_ALLOWED = 'NotAllowedError';
const SAFARI_ERROR_NAME_ABORT = 'AbortError';
const DEBOUNCE_DURATION = 100;
const SAFARI_AUTOPLAY_ERRORS = [SAFARI_ERROR_NAME_ABORT, SAFARI_ERROR_NAME_NOT_ALLOWED];

const createIsAutoplayRejection = () => {
    let debounceId = null;

    return (error: Error): boolean => {
        const {name, message} = error;

        if (!SAFARI_AUTOPLAY_ERRORS.includes(name) && debounceId) {
            clearTimeout(debounceId);

            debounceId = null;
        } else if (SAFARI_AUTOPLAY_ERRORS.includes(name)) {
            if (!debounceId) {
                debounceId = setTimeout(() => debounceId = null, DEBOUNCE_DURATION);

                return true;
            } else if (name === SAFARI_ERROR_NAME_ABORT) {
                return false;
            }
        }

        if (name === SAFARI_ERROR_NAME_NOT_ALLOWED) return true;

        return !message.match(/(pause|load)/gmi);
    };
};

export {
    createIsAutoplayRejection as default,
    SAFARI_ERROR_NAME_ABORT,
    SAFARI_ERROR_NAME_NOT_ALLOWED
};